// src/tabs/messages/DirectPreviewScenarioPanel.jsx — PARTIE B / B5 (ADR-272)
//
// Panneau « Simuler une réservation » : les cases à cocher de l'APERÇU INTERACTIF
// Direct. Monté dans la COLONNE DROITE, JUSTE AU-DESSUS de l'aperçu — exigence
// produit Dimitri : « sur le même écran que l'éditeur ».
//
// ⚠️ COSMÉTIQUE PURE — ZÉRO ÉCRITURE, ZÉRO ENVOI : ce composant ne fait que
// remonter un objet `scenario` au parent, qui recalcule le contexte d'aperçu en
// mémoire. Aucun fetch, aucun accès base, aucun email. Le scénario n'entre JAMAIS
// dans le brouillon (`draft`) → le bouton « Enregistrer » ne s'allume pas et la
// garde brouillon ne se déclenche pas.
//
// Le rendu est DATA-DRIVEN : il déroule DIRECT_SCENARIO_CONTROLS
// (src/lib/direct-preview-scenario.js). Ajouter un contrôle = ajouter une entrée
// au descripteur — pas de JSX à réécrire. Le seuil de caution affiché vient de
// CAUTION_HOLD_MAX_NIGHTS (ADR-174) : jamais de « 4 » en dur ici.
//
// ⚠️ COMPACITÉ = EXIGENCE PRODUIT (QA #1095). Déplié, ce panneau faisait ~800 px
// et repoussait l'aperçu sous la ligne de flottaison : le bénéfice « je coche et
// je vois l'aperçu changer EN DIRECT » était perdu hors très grand écran. Les
// groupes sont donc REPLIÉS par défaut ; seuls s'ouvrent ceux dont un drapeau est
// RÉELLEMENT utilisé par le gabarit affiché (`openGroups`, calculé par le parent
// via scenarioGroupsForTexts). Un gabarit sans `{#if}` → panneau entièrement
// replié (~5 lignes). Un groupe replié qui porte une valeur MODIFIÉE affiche une
// pastille : on ne cache jamais un réglage actif derrière un chevron.

import {
  ChevronDown,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useState } from "react";
import { DS } from "../../lib/ui";
import {
  DEFAULT_DIRECT_SCENARIO,
  DIRECT_SCENARIO_CONTROLS,
  DIRECT_SCENARIO_GROUPS,
} from "../../lib/direct-preview-scenario";

const LABEL_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12.5,
  color: DS.marineDark,
  cursor: "pointer",
  lineHeight: 1.35,
};

function BoolControl({ ctl, value, onChange }) {
  return (
    <label style={LABEL_STYLE}>
      <input
        type="checkbox"
        data-testid={`direct-scenario-${ctl.id}`}
        checked={value === true}
        onChange={(e) => onChange(ctl.id, e.target.checked)}
      />
      <span>{ctl.label}</span>
    </label>
  );
}

function ChoiceControl({ ctl, value, onChange }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: DS.textMuted,
          marginBottom: 5,
        }}
      >
        {ctl.label}
      </div>
      <div
        data-testid={`direct-scenario-${ctl.id}`}
        style={{
          display: "flex",
          background: DS.pageBg,
          borderRadius: 9,
          padding: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {ctl.options.map((opt) => {
          const on = String(value ?? "") === String(opt.value);
          return (
            <button
              key={opt.value || "__none__"}
              type="button"
              data-testid={`direct-scenario-${ctl.id}-${opt.value || "none"}`}
              aria-pressed={on}
              onClick={() => onChange(ctl.id, opt.value)}
              style={{
                flex: "1 1 auto",
                border: "none",
                background: on ? DS.card : "transparent",
                color: on ? DS.marine : DS.textMuted,
                boxShadow: on ? "0 1px 3px rgba(20,51,80,.14)" : "none",
                borderRadius: 6,
                padding: "5px 8px",
                fontFamily: DS.fontBody,
                fontSize: 11.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {ctl.help && (
        <div style={{ fontSize: 10.5, color: DS.textMuted, marginTop: 4 }}>
          {ctl.help}
        </div>
      )}
    </div>
  );
}

function NumberControl({ ctl, value, onChange }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: DS.textMuted,
          marginBottom: 4,
        }}
      >
        {ctl.label}
      </div>
      <input
        type="number"
        data-testid={`direct-scenario-${ctl.id}`}
        value={value ?? ""}
        placeholder={ctl.placeholder || ""}
        onChange={(e) => onChange(ctl.id, e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "6px 9px",
          fontFamily: DS.fontBody,
          fontSize: 12.5,
          color: DS.marineDark,
          background: DS.card,
        }}
      />
    </label>
  );
}

function TextControl({ ctl, value, onChange }) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: DS.textMuted,
          marginBottom: 4,
        }}
      >
        {ctl.label}
      </div>
      <textarea
        rows={2}
        data-testid={`direct-scenario-${ctl.id}`}
        value={value ?? ""}
        placeholder={ctl.placeholder || ""}
        onChange={(e) => onChange(ctl.id, e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: `1px solid ${DS.border}`,
          borderRadius: 8,
          padding: "6px 9px",
          fontFamily: DS.fontBody,
          fontSize: 12.5,
          lineHeight: 1.45,
          color: DS.marineDark,
          background: DS.card,
          resize: "vertical",
        }}
      />
    </label>
  );
}

const RENDERERS = {
  bool: BoolControl,
  choice: ChoiceControl,
  number: NumberControl,
  text: TextControl,
};

// Un groupe est « modifié » dès qu'un de ses contrôles s'écarte du scénario
// nominal — sert la pastille des groupes repliés.
function groupIsDirty(group, value) {
  return DIRECT_SCENARIO_CONTROLS.some(
    (c) =>
      c.group === group &&
      String(value[c.id] ?? "") !== String(DEFAULT_DIRECT_SCENARIO[c.id] ?? ""),
  );
}

/**
 * @param {{
 *   scenario: object,
 *   onChange: (next: object) => void,
 *   openGroups?: string[],  // groupes ouverts d'emblée (drapeaux utilisés par le gabarit)
 *   lang?: string,          // langue de l'aperçu ('fr' | 'en' | 'es')
 * }} props
 */
export default function DirectPreviewScenarioPanel({
  scenario,
  onChange,
  openGroups,
  lang,
}) {
  const value = { ...DEFAULT_DIRECT_SCENARIO, ...(scenario || {}) };

  // Clé stable : re-synchronise l'ouverture quand le gabarit affiché change
  // (autres `{#if}` → autres groupes pertinents), sans écraser un pli manuel.
  const openKey = (openGroups || []).join("|");
  const [open, setOpen] = useState(() => new Set(openGroups || []));
  useEffect(() => {
    setOpen(new Set(openKey ? openKey.split("|") : []));
  }, [openKey]);

  const toggleGroup = (g) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });

  const setField = (id, v) => {
    if (typeof onChange !== "function") return;
    onChange({ ...value, [id]: v });
  };

  return (
    <div
      data-testid="direct-preview-scenario"
      style={{
        background: DS.card,
        border: `1px solid ${DS.border}`,
        borderRadius: 14,
        padding: "13px 14px",
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: DS.marine,
          }}
        >
          <SlidersHorizontal size={13} strokeWidth={2} /> Simuler une
          réservation
        </div>
        <button
          type="button"
          data-testid="direct-scenario-reset"
          onClick={() => onChange && onChange({ ...DEFAULT_DIRECT_SCENARIO })}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            border: `1px solid ${DS.border}`,
            background: DS.card,
            color: DS.textMuted,
            borderRadius: 8,
            padding: "4px 8px",
            fontFamily: DS.fontBody,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <RotateCcw size={11} strokeWidth={2} /> Réinitialiser
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {DIRECT_SCENARIO_GROUPS.map((group) => {
          const isOpen = open.has(group);
          const dirty = groupIsDirty(group, value);
          return (
            <div key={group}>
              <button
                type="button"
                data-testid={`direct-scenario-group-${group}`}
                aria-expanded={isOpen}
                onClick={() => toggleGroup(group)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  padding: "4px 0",
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: DS.marineDark,
                  textAlign: "left",
                }}
              >
                {isOpen ? (
                  <ChevronDown size={12} strokeWidth={2.5} />
                ) : (
                  <ChevronRight size={12} strokeWidth={2.5} />
                )}
                <span>{group}</span>
                {!isOpen && dirty && (
                  <span
                    data-testid={`direct-scenario-group-dirty-${group}`}
                    title="Réglage modifié dans ce groupe"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: DS.marine,
                      flex: "0 0 auto",
                    }}
                  />
                )}
              </button>
              {isOpen && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    padding: "4px 0 8px 17px",
                  }}
                >
                  {DIRECT_SCENARIO_CONTROLS.filter(
                    (c) => c.group === group,
                  ).map((ctl) => {
                    const Renderer = RENDERERS[ctl.kind];
                    if (!Renderer) return null;
                    return (
                      <Renderer
                        key={ctl.id}
                        ctl={ctl}
                        value={value[ctl.id]}
                        onChange={setField}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bandeau honnête (règle « texte d'aide = état réel ») : ce panneau ne
          simule QUE l'affichage. Les fragments partagés {{frag:…}} sont
          pré-assemblés par le serveur AVANT l'envoi — l'aperçu les laisse en
          clair. */}
      <div
        data-testid="direct-scenario-footnote"
        style={{
          marginTop: 11,
          paddingTop: 9,
          borderTop: `1px solid ${DS.border}`,
          fontSize: 10.5,
          lineHeight: 1.5,
          color: DS.textMuted,
        }}
      >
        Simple aperçu : rien n'est enregistré, aucun email ne part. Les morceaux
        réutilisables apparaissent ici sous forme de repère ; ils sont remplacés
        par leur texte au moment de l'envoi.
        {String(lang || "").startsWith("es") && (
          <>
            {" "}
            <strong>
              Le texte espagnol affiché ici n'est pas encore celui qui part
            </strong>{" "}
            — voir l'avertissement au-dessus de l'aperçu.
          </>
        )}
      </div>
    </div>
  );
}
